/** @jsx jsx */
import { useState, useEffect, useRef, Fragment } from 'react'
import { css, jsx, keyframes } from '@emotion/core'
import { createMessageBoxWidget } from '@livechat/agent-app-sdk'
import { SearchBar, Loader } from '@livechat/design-system'

import '@livechat/design-system/dist/design-system.css'

import { useAuth } from '../hooks'
import api from '../api'
import SurveyLabelsSelectFilter from '../components/SurveyLabelsSelectFilter'
import { formatItem } from '../components/SurveyLabels'
import CopyButton from '../components/CopyButton'
import { checkLocalStorage } from '../utils'

const showAnimation = keyframes`
 from {
  transform: scale(0.95);
 }
`

const showLoadingAnimate = keyframes`
  from {
    transform: scale(0.8);
  }

  to {
    opacity: 1;
  }
`

const appCss = css`
  width: 100%;
  height: 100vh;
  padding-bottom: 0;
  overflow: hidden;

  > div:first-of-type {
    padding: 10px;
    padding-bottom: 0;
    width: calc(100% - 20px);
  }

  .surveys {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-x: auto;
    -ms-overflow-style: none;
    padding: 10px;
    padding-top: 10px;
    padding-bottom: 15px;
  }

  .loader {
    width: 100%;
    display: flex;
    justify-content: center;

    > img {
      transform: scale(0.8);
      margin-top: -20px;
    }
  }

  .info {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    font-size: 14px;

    > img {
      transform: rotate(45deg);
      margin-right: 35px;
      margin-top: 15px;
      width: 18px;
    }
  }

  .text {
    display: block;
    margin-right: 5px;
    margin-top: 10px;
    font-weight: 500;
    font-size: 14px;
    user-select: none;
    transition: all 0.2s ease-in-out;
    animation: ${showAnimation} 0.2s ease;
  }

  .text-top {
    margin-left: 10px;
    animation: none;
  }

  .lc-btn {
    width: 100px;
    height: 30px;
  }
`

const surveyCss = (selected, type, index) => css`
  width: calc(100% - 30px);
  margin-bottom: 10px;
  display: flex;
  background-color: white;
  margin-right: 10px;
  padding: 10px 15px;
  /* cursor: pointer; */
  border: 1px solid
    ${type !== 'loading' && selected ? 'rgb(67, 132, 245)' : '#cccccc'};
  border-radius: 7px;
  position: relative;

  /* :hover {
    ${type !== 'loading' &&
  css`
    border-color: ${selected ? 'rgb(67, 132, 245)' : 'rgba(67, 132, 245, 0.5)'};
  `}
  } */

  .name {
    font-size: 14px;
    margin-bottom: 5px;
    overflow: scroll;
    padding-top: 5px;
    font-weight: 300;
  }

  .amount {
    user-select: none;
    font-size: 13px;
    color: hsl(0, 0%, 70%);
  }

  .selected {
    width: 18px;
    position: absolute;
    top: -7px;
    right: -7px;
  }

  ${type === 'disabled' &&
  css`
    cursor: not-allowed;

    .name {
      color: rgba(67, 78, 88, 0.5);
    }

    :hover {
      border-color: #cccccc;
    }
  `}

  ${type === 'loading' &&
  css`
    .name-placeholder {
      width: 70%;
      height: 10px;
      background-color: rgba(67, 78, 88, 0.1);
      border-radius: 20px;
      margin-top: 5px;
      margin-left: 15%;
    }

    .amount-placeholder {
      width: 50%;
      height: 10px;
      background-color: rgba(67, 78, 88, 0.1);
      border-radius: 20px;
      margin-left: 25%;
    }

    opacity: 0;
    animation: ${showLoadingAnimate} 1s ease ${`0.${index}s`} forwards;
    transition: all 0.2s ease-in-out;
  `}
`

const loaderCss = css`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .lc-loader-label {
    margin-right: 0;
    margin-left: 5px;
  }
`

const App = () => {
  const { accessToken: livechatToken, agentId } = useAuth()
  const [surveyToken, setSurveyToken] = useState()
  const [surveys, setSurveys] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSurveys, setSelectedSurveys] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [loadingPublicToken, setLoadingPublicToken] = useState()
  const [settings, setSettings] = useState()
  const [activeLabelsFilters, setActiveLabelsFilters] = useState([])
  const [isSettingsLoading, setIsSettingsLoading] = useState(true)

  useEffect(() => {
    const request = async () => {
      setLoading(true)
      const data = await api.getSurveyToken(livechatToken, agentId)
      const { survey_token } = JSON.parse(data)
      setSurveyToken(survey_token)
      setLoading(false)
    }

    if (livechatToken && !surveyToken) {
      request()
    }
  }, [livechatToken])

  useEffect(() => {
    const request = async () => {
      setLoading(true)
      const data = await api.getSurveys(surveyToken)
      setSurveys(data)
      setLoading(false)
    }

    if (surveyToken) {
      request()
    }
  }, [surveyToken])

  useEffect(() => {
    const request = async () => {
      setIsSettingsLoading(true)
      const data = await api.fetchSettings(surveyToken)
      const formattedSettings = JSON.parse?.(data)
      setSettings(JSON.parse?.(data))

      if (checkLocalStorage()) {
        const activeLabelsFilters =
          JSON.parse?.(localStorage.getItem('activeLabelsFilters')) || []

        const availableLabels = activeLabelsFilters.filter((l) =>
          formattedSettings.grouping_labels.includes(l.props.value)
        )

        setActiveLabelsFilters(availableLabels)
      }
      setIsSettingsLoading(false)
    }

    if (surveyToken) {
      request()
    }
  }, [surveyToken])

  const updateLabelFilters = (value) => {
    if (checkLocalStorage()) {
      localStorage.setItem('activeLabelsFilters', JSON.stringify(value))
    }

    setActiveLabelsFilters(value)
  }

  const widget = useRef(null)

  useEffect(() => {
    const request = async () => {
      try {
        const messageBoxWidget = await createMessageBoxWidget()
        widget.current = messageBoxWidget
      } catch (error) {
        console.log(error)
      }
    }

    request()
  }, [])

  const getNumberOfQuestions = (id) => {
    try {
      const structure = surveys.find((survey) => survey.id === id).structure
      const questions =
        typeof structure === 'string' ? JSON.parse(structure) : structure

      return questions.length
    } catch (err) {
      console.log(err)
      return 0
    }
  }

  const renderLoading = () =>
    [1, 2, 3, 4, 5, 6].map((index) => (
      <div
        css={surveyCss(false, 'loading', index)}
        key={`loading-survey-${index}`}
      >
        <div
          css={css`
            width: 100%;
            height: 30px;
          `}
        />
      </div>
    ))

  const filteredSurveys = surveys.filter((el) => {
    const isMatchedByName = searchValue
      ? el.name?.toLowerCase()?.includes(searchValue?.toLowerCase())
      : true

    const surveyLabels = el?.grouping_labels || []

    const isMatchedByLabel =
      activeLabelsFilters.length > 0
        ? activeLabelsFilters.every((l) =>
            surveyLabels.includes(l?.props?.value)
          )
        : true

    return isMatchedByName && isMatchedByLabel
  })

  const labelsExist = settings?.grouping_labels?.length > 0

  return (
    <div css={appCss}>
      <div
        css={css`
          width: 100%;
          display: flex;
          flex-direction: column;
        `}
      >
        <SearchBar
          placeholder="Search for a survey.."
          onChange={setSearchValue}
          value={searchValue}
          css={css`
            width: 100%;
            ${labelsExist &&
            css`
              margin-bottom: 10px;
            `}

            .lc-search-bar__container {
              width: 100%;
            }
          `}
        />
        {labelsExist && (
          <div
            css={css`
              width: 100%;
            `}
          >
            <SurveyLabelsSelectFilter
              isLoading={isSettingsLoading}
              selectedItems={activeLabelsFilters}
              setSelectedItems={updateLabelFilters}
              licenseGroupingLabels={
                settings?.grouping_labels?.map((l) => formatItem(l)) || []
              }
            />
          </div>
        )}
      </div>
      <div className="surveys">
        {loading ? (
          renderLoading()
        ) : surveys.length > 0 ? (
          <Fragment>
            {filteredSurveys
              .filter((el) => el.active)
              .map((el) => {
                const isSelected = selectedSurveys.includes(el.id)
                const numberOfQuestions = getNumberOfQuestions(el.id)

                if (loadingPublicToken === el.id) {
                  return (
                    <div css={surveyCss(false)} key={el.id}>
                      <div css={loaderCss}>
                        <div>
                          <Loader label="Preparing survey.." size="small" />
                        </div>
                      </div>
                    </div>
                  )
                }

                return (
                  <div css={surveyCss(false)} key={el.id}>
                    <div css={css``}>
                      <div className="name">{el.name}</div>
                      <div className="amount">
                        {numberOfQuestions > 1
                          ? `${numberOfQuestions} questions`
                          : `${numberOfQuestions} question`}
                      </div>
                    </div>
                    <CopyButton
                      copy={
                        el.publicSurveyId
                          ? `${process.env.REACT_APP_SURVEYS_SHORT}/public/${el.publicSurveyId}`
                          : `${process.env.REACT_APP_SURVEYS}/chat-widget/${el.publicToken}`
                      }
                      text="Copy link"
                      textAfterCopy="Copied!"
                    />
                  </div>
                )
              })}
            {filteredSurveys
              .filter((el) => !el.active)
              .map((el) => {
                const numberOfQuestions = getNumberOfQuestions(el.id)

                return (
                  <div css={surveyCss(false, 'disabled')} key={el.id}>
                    <div className="name">{el.name}</div>
                    <div className="amount">
                      {numberOfQuestions > 1
                        ? `${numberOfQuestions} questions`
                        : `${numberOfQuestions} question`}
                    </div>
                  </div>
                )
              })}
          </Fragment>
        ) : (
          <div>No surveys to send.</div>
        )}
      </div>
    </div>
  )
}

export default App
